import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isNull } from 'lodash-es';

import styled from 'styled-components';

import {
    SaveDraftIcon,
    ScheduledSendIcon,
    SendCampaignBtnIcon,
    SecondaryButton,
    WebsitePrimaryButton,
    TertiaryButton,
    palette,
} from '@dev/ee-components';
import { CampaignType, CampaignTriggerType } from '@dev/ee-api/ee-api-models';

import dayjs from '@/services/tools/dayjs';
import { HideDialog, ShowDialog } from '@/store/dialog/actions';
import { ShowDialogParams } from '@/store/dialog/types';
import { getCampainCreatorStore, SaveCampaignStart, ModifyCampaignMap } from '@/store/campaignCreator/actions';
import { LONG_DATE_FORMAT } from '@/services/constants';
import { useReshardingActions } from '@/services/hooks/useReshardingActions';
import { useRouteMatch } from 'react-router';
import { pathsParams } from '@/services/dictionaries/Paths';
import { generateIdBasedOnPath } from '@dev/ee-tools';
import { ScheduleOptions } from '../ScheduleOptions';
import { SendModal } from '../SendModal';
import { useHasCampaignAnyTemplates } from '../hooks/useHasCampaignAnyTemplates';

const FooterContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    column-gap: 12px;
    row-gap: 24px;
    justify-content: space-between;
    align-items: center;
`;

interface ButtonsWrapperProps {
    isSendDisabled: boolean;
}

const ButtonsWrapper = styled.div<ButtonsWrapperProps>`
    display: flex;
    gap: 12px;
    button {
        svg {
            fill: ${({ isSendDisabled }) => (isSendDisabled ? palette.gray[4] : palette.indigo[6])};
        }
        &:first-child svg {
            fill: ${({ isSendDisabled }) => (isSendDisabled ? palette.gray[4] : palette.basic.paper)};
        }
    }
`;

const IconWrapper = styled.div`
    button {
        svg {
            fill: ${palette.indigo[6]};
        }
    }
`;

export const Footer = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { campaign, isRecipientsCountLoading, isSaving, recipientsCount } = useSelector(getCampainCreatorStore);

    const hasTemplates = useHasCampaignAnyTemplates();
    const isAutomatedCampaign = campaign?.Type === CampaignType.Automated;
    const isRecipientsCountLoad = isAutomatedCampaign ? false : isRecipientsCountLoading;
    const isSendDisabled = !hasTemplates || isRecipientsCountLoad;
    const route = useRouteMatch<{ type: string }>();

    const routeForIds =
        route?.path?.replace?.(pathsParams.name, '')?.replace(pathsParams.type, route.params?.type) ?? '';

    const saveDraft = (redirectURL?: string): void => {
        dispatch<any>(SaveCampaignStart({ isDraft: true, redirectURL })).then(() => {
            dispatch(HideDialog());
        });
    };

    const { showReshardDialog } = useReshardingActions();

    const send = (): void => {
        const modifyMap = new Map<string, any>();
        if (campaign?.TriggerOptions?.Type === CampaignTriggerType.FutureScheduled) {
            modifyMap.set('TriggerOptions.DateToSendOn', null);
            modifyMap.set('TriggerOptions.Type', CampaignTriggerType.SendNow);
            modifyMap.set('TriggerOptions.SendXTimes', 0);
            modifyMap.set('TriggerOptions.SendEveryXMinutes', 0);
            dispatch(ModifyCampaignMap(modifyMap));
        }
        if (campaign?.TriggerOptions?.Type !== CampaignTriggerType.OnClick) {
            modifyMap.set('TriggerOptions.TriggeredBy.Data', null);
            dispatch(ModifyCampaignMap(modifyMap));
        }
        dispatchSaveCampaign();
    };

    const dispatchSaveCampaign = () => {
        dispatch<any>(SaveCampaignStart({ isDraft: false }))
            .then(() => {
                dispatch(HideDialog());
            })
            .catch(() => {
                showReshardDialog();
            });
    };

    const schedule = (): void => {
        dispatchSaveCampaign();
    };

    const openScheduleModal = (): void => {
        const modifyMap = new Map<string, any>();
        if (isNull(campaign?.TriggerOptions?.DateToSendOn)) {
            modifyMap.set('TriggerOptions.DateToSendOn', dayjs().add(1, 'hour').utc().format(LONG_DATE_FORMAT));
            modifyMap.set('TriggerOptions.Type', CampaignTriggerType.FutureScheduled);
            dispatch(ModifyCampaignMap(modifyMap));
        }

        const scheduleModalParams: ShowDialogParams = {
            title: t('schedulecampaign'),
            cancelLabel: t('cancel'),
            confirmLabel: t('schedule'),
            onClickConfirm: schedule,
            body: <ScheduleOptions />,
            isHiddenManually: true,
        };
        dispatch(ShowDialog(scheduleModalParams));
    };

    const openSendModal = (): void => {
        const params: ShowDialogParams = {
            title: t('readytosendquestion'),
            cancelLabel: t('cancel'),
            confirmLabel: t('send'),
            onClickConfirm: send,
            body: <SendModal recipientsCount={recipientsCount} />,
            isHiddenManually: true,
        };
        dispatch(ShowDialog(params));
    };

    const openSetAutoresponderModal = (): void => {
        const params: ShowDialogParams = {
            title: t('readytosetautoresponder'),
            cancelLabel: t('cancel'),
            confirmLabel: t('setautoresponder'),
            onClickConfirm: send,
            body: t('yourautoresponderisreadytoset'),
        };
        dispatch(ShowDialog(params));
    };

    return (
        <FooterContainer>
            <ButtonsWrapper isSendDisabled={isSendDisabled}>
                <WebsitePrimaryButton
                    id={generateIdBasedOnPath('send-campaign-button', routeForIds)}
                    text={isAutomatedCampaign ? t('setautoresponder') : t('sendnow')}
                    startIcon={<SendCampaignBtnIcon />}
                    onClick={isAutomatedCampaign ? openSetAutoresponderModal : openSendModal}
                    disabled={isSendDisabled}
                />
                {!isAutomatedCampaign && (
                    <SecondaryButton
                        id={generateIdBasedOnPath('schedule-campaign-button', routeForIds)}
                        text={t('schedule')}
                        onClick={openScheduleModal}
                        startIcon={<ScheduledSendIcon />}
                        disabled={isSendDisabled}
                    />
                )}
            </ButtonsWrapper>
            <IconWrapper>
                <TertiaryButton
                    id={generateIdBasedOnPath('save-as-draft-button', routeForIds)}
                    text={t('saveasdraft')}
                    disabled={isSaving}
                    startIcon={<SaveDraftIcon />}
                    onClick={() => saveDraft()}
                />
            </IconWrapper>
        </FooterContainer>
    );
};
